import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import de from './de.json';
import enGB from './en_GB.json';
import enUS from './en_US.json';
import fi from './fi.json';
import nl from './nl.json';
import sr from './sr.json';
import sv from './sv.json';

const LANGUAGE_CONFIG = {
  de: { translation: de, label: 'Deutsch', code: 'de' },
  'en-GB': { translation: enGB, label: 'English (GB)', code: 'en-GB' },
  'en-US': { translation: enUS, label: 'English (US)', code: 'en-US' },
  fi: { translation: fi, label: 'Suomi', code: 'fi' },
  nl: { translation: nl, label: 'Nederlands', code: 'nl' },
  sr: { translation: sr, label: 'Srpski', code: 'sr' },
  sv: { translation: sv, label: 'Svenska', code: 'sv' },
} as const;

const supportedLanguages = Object.values(LANGUAGE_CONFIG).map(
  ({ label, code }) => ({ label, code }),
);

type SupportedLanguageCodes = keyof typeof LANGUAGE_CONFIG;

const SUPPORTED_LANGUAGE_CODES = Object.keys(
  LANGUAGE_CONFIG,
) as SupportedLanguageCodes[];

function isSupportedLanguage(s: string): s is SupportedLanguageCodes {
  return SUPPORTED_LANGUAGE_CODES.some((code) => code === s);
}

const LANGUAGE_KEY = 'language';
const PRIMARY_LANGUAGE: SupportedLanguageCodes = 'en-GB';

const getInitialLanguage = (): SupportedLanguageCodes => {
  const stored = localStorage.getItem(LANGUAGE_KEY);
  if (stored !== null && isSupportedLanguage(stored)) return stored;

  const browserLang =
    typeof navigator !== 'undefined' ? navigator.language : null;
  if (browserLang !== null && isSupportedLanguage(browserLang))
    return browserLang;

  return PRIMARY_LANGUAGE;
};

const saveLanguageToLocalStorage = (lang: string) =>
  localStorage.setItem(LANGUAGE_KEY, lang);

const ACTIVE_LANGUAGE: SupportedLanguageCodes = getInitialLanguage();

i18n.use(initReactI18next).init({
  resources: LANGUAGE_CONFIG,
  lng: ACTIVE_LANGUAGE,
  fallbackLng: PRIMARY_LANGUAGE,
  returnEmptyString: false,
  interpolation: {
    escapeValue: false,
  },
});

export {
  saveLanguageToLocalStorage,
  supportedLanguages,
  type SupportedLanguageCodes,
};

export default i18n;
